import { useState } from "react";
import { Link } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, FileText, ChevronLeft, Loader2, Info, ArrowRight } from "lucide-react";
import sentinelforgeLogo from "@/assets/sentinelforge-logo.webp";

const propertyTypes = [
  "retail", "warehouse", "school", "hospital", "residential",
  "industrial", "office", "construction_site", "event_venue", "parking_lot",
];

const serviceTypes = [
  { value: "standing_guard", label: "Standing Guard" },
  { value: "mobile_patrol", label: "Mobile Patrol" },
  { value: "fire_watch", label: "Fire Watch" },
  { value: "event_security", label: "Event Security" },
  { value: "loss_prevention", label: "Loss Prevention" },
];

interface DemoResults {
  riskLevel: string;
  executiveSummary: string;
  recommendation: string;
  estimatedCost: string;
  threatAssessment: string;
  implementationPlan: string;
  crimeDataSummary: string;
  expectedOutcomes: string;
}

const DemoBuilder = () => {
  const [address, setAddress] = useState("450 Commerce Blvd, Dallas, TX");
  const [propertyType, setPropertyType] = useState("retail");
  const [serviceType, setServiceType] = useState("standing_guard");
  const [officers, setOfficers] = useState(2);
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<DemoResults | null>(null);

  const handleGenerate = async () => {
    setIsLoading(true);
    // Simulate AI generation with a delay
    await new Promise((r) => setTimeout(r, 2000));

    const riskMap: Record<string, string> = {
      retail: "High", warehouse: "Moderate", school: "High",
      hospital: "Critical", residential: "Low", industrial: "Moderate",
      office: "Low", construction_site: "Moderate", event_venue: "High", parking_lot: "Moderate",
    };

    const risk = riskMap[propertyType] || "Moderate";
    const weeklyRate = officers * 8 * 5 * 28;

    const serviceName = serviceTypes.find(s => s.value === serviceType)?.label || serviceType;
    const ptLabel = propertyType.replace(/_/g, " ");

    setResults({
      riskLevel: risk,
      executiveSummary: `Based on our analysis of the ${ptLabel} property at ${address}, we recommend a comprehensive ${serviceName} deployment. The area presents ${risk.toLowerCase()} risk factors including foot traffic patterns, access point vulnerabilities, and historical incident data. Our recommendation of ${officers} officer${officers > 1 ? "s" : ""} provides optimal coverage for this facility type.`,
      recommendation: `Deploy ${officers} ${serviceType.replace(/_/g, " ")} officer${officers > 1 ? "s" : ""} with 8-hour shifts, 5 days per week. Include patrol routes covering all access points, parking areas, and high-traffic zones. Implement daily reporting and incident documentation protocols.`,
      estimatedCost: `$${weeklyRate.toLocaleString()}/week ($${(weeklyRate * 4).toLocaleString()}/month)`,
      threatAssessment: `The ${ptLabel} property at ${address} has been assessed for key vulnerability factors. Primary concerns include ${risk === "Critical" || risk === "High" ? "elevated crime rates in the surrounding area, multiple unsecured access points, and high foot traffic during peak hours" : "moderate foot traffic patterns and standard access point configurations"}. Perimeter security shows ${risk === "Low" ? "adequate" : "gaps in"} coverage, particularly at ${ptLabel === "retail" ? "loading docks and rear entrances" : "secondary entry points and parking areas"}. Environmental factors such as lighting conditions, nearby transit routes, and adjacent property usage have been factored into this assessment.`,
      crimeDataSummary: `Area crime statistics for the surrounding zip code indicate ${risk === "Critical" ? "significantly above-average" : risk === "High" ? "above-average" : risk === "Moderate" ? "average" : "below-average"} incident rates. Property crimes account for approximately ${risk === "High" || risk === "Critical" ? "67%" : "42%"} of reported incidents, with theft and vandalism being the most common. Violent crime rates are ${risk === "Critical" ? "elevated" : "within normal ranges"}. Peak incident times occur between 10 PM and 3 AM, with secondary spikes during shift changes.`,
      implementationPlan: `Phase 1 (Week 1-2): Site survey, access point mapping, and patrol route design. Phase 2 (Week 3): Officer onboarding, site-specific training, and communication setup. Phase 3 (Week 4): Full deployment of ${officers} ${serviceType.replace(/_/g, " ")} officer${officers > 1 ? "s" : ""} with established check-in protocols. Ongoing: Weekly performance reviews, monthly incident reporting, and quarterly security plan updates.`,
      expectedOutcomes: `With the recommended ${serviceName} deployment, expect a 40-60% reduction in security incidents within the first 90 days. Visible security presence typically reduces theft by 50-70% and trespassing by 60-80%. Client satisfaction benchmarks target 95%+ positive feedback. Insurance premium reductions of 10-20% are commonly achieved within the first year of professional security deployment.`,
    });
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={sentinelforgeLogo} alt="Sentinel Forge" className="h-7 w-7 object-contain" />
            <span className="text-lg font-bold text-foreground tracking-tight">Sentinel Forge</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/auth">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">Sign In</Button>
            </Link>
            <Link to="/auth?signup">
              <Button size="sm" className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">Start Free Trial</Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-20 max-w-4xl mx-auto px-6">
        {/* Demo Banner */}
        <Alert className="mb-6 border-primary/30 bg-primary/5">
          <Info className="h-4 w-4 text-primary" />
          <AlertDescription className="text-sm text-muted-foreground">
            <strong>This is a simplified demo.</strong> Real proposals generated with a free account are significantly longer and more in-depth — including detailed AI threat assessments, crime data analysis, comprehensive staffing plans, implementation timelines, and professional PDF export. <Link to="/auth?signup" className="text-primary font-medium hover:underline">Create a free account</Link> to experience the full platform.
          </AlertDescription>
        </Alert>

        <div className="flex items-center gap-2 mb-6">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
            <ChevronLeft className="h-4 w-4" /> Back to Home
          </Link>
        </div>

        <h1 className="text-3xl font-bold text-foreground mb-2">Demo Proposal Builder</h1>
        <p className="text-muted-foreground mb-8">Try the security proposal builder — no account required.</p>

        <div className="space-y-6">
          {/* Form */}
          <Card className="border-border bg-card shadow-card">
            <CardHeader>
              <CardTitle className="text-lg">Property & Service Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Property Address</Label>
                <Input value={address} onChange={(e) => setAddress(e.target.value)} className="mt-1 bg-secondary border-border text-foreground" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Property Type</Label>
                  <Select value={propertyType} onValueChange={setPropertyType}>
                    <SelectTrigger className="mt-1 bg-secondary border-border text-foreground">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyTypes.map((pt) => (
                        <SelectItem key={pt} value={pt}>{pt.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-muted-foreground">Service Type</Label>
                  <Select value={serviceType} onValueChange={setServiceType}>
                    <SelectTrigger className="mt-1 bg-secondary border-border text-foreground">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map((st) => (
                        <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Number of Officers</Label>
                  <Input type="number" min={1} max={20} value={officers} onChange={(e) => setOfficers(Number(e.target.value) || 1)} className="mt-1 bg-secondary border-border text-foreground" />
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Additional Notes (optional)</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any special requirements..." className="mt-1 bg-secondary border-border text-foreground" rows={3} />
              </div>
              <Button
                onClick={handleGenerate}
                disabled={isLoading || !address}
                className="w-full gradient-primary text-primary-foreground shadow-glow hover:opacity-90"
              >
                {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating Demo Proposal...</> : <><FileText className="mr-2 h-4 w-4" /> Generate Demo Proposal</>}
              </Button>
            </CardContent>
          </Card>

          {/* Results */}
          {results && (
            <Card className="border-border bg-card shadow-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Demo Proposal Results</CardTitle>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    results.riskLevel === "Critical" ? "bg-destructive/20 text-destructive" :
                    results.riskLevel === "High" ? "bg-destructive/10 text-destructive" :
                    results.riskLevel === "Moderate" ? "bg-warning/20 text-warning" :
                    "bg-success/20 text-success"
                  }`}>
                    {results.riskLevel} Risk
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">Executive Summary</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{results.executiveSummary}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">Threat Assessment</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{results.threatAssessment}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">Area Crime Data Analysis</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{results.crimeDataSummary}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">Staffing Recommendation</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{results.recommendation}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">Implementation Plan</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{results.implementationPlan}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">Expected Outcomes</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{results.expectedOutcomes}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">Estimated Cost</h3>
                  <p className="text-lg font-bold text-primary">{results.estimatedCost}</p>
                </div>

                <div className="border-t border-border pt-6">
                  <div className="rounded-xl border border-primary/20 bg-primary/5 p-6 text-center">
                    <p className="text-foreground font-semibold mb-2">This is still just a preview</p>
                    <p className="text-sm text-muted-foreground mb-4">
                      Full proposals include even more detail — multi-page formatted documents with your company branding, detailed cost breakdowns with line items, contract generation with e-signature, client portal sharing, proposal history tracking, and professional PDF export ready to send to clients.
                    </p>
                    <Link to="/auth?signup">
                      <Button className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">
                        Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default DemoBuilder;
